// The connection manager (./pusher-connection) and pusher-js with it, about 18 KB gzipped,
// load lazily: no page needs a socket before it paints, and a static import put them in the
// first wave every boot downloads, sharing the line with the chunks the first paint waits on.
// The hooks (usePusherChannel, usePusherHold) start the download when their module evaluates,
// with the app's own chunk, so by the first subscribe the manager is usually in and they run
// synchronously, exactly as before. When it is not in yet, they set up once it lands.
//
// A load that fails leaves the tab without live events. It is logged and reported as a
// `load_error` health signal (RUM). A failed module import cannot be retried in place (the
// browser keeps the failure for the document), so the next page load is the recovery.

import { emitPusherHealth } from "./pusher-health";

type PusherConnection = typeof import("./pusher-connection");

let loaded: PusherConnection | null = null;
let loading: Promise<PusherConnection> | null = null;

/** Start the connection manager's download (once) and resolve with it. */
export function loadPusherConnection(): Promise<PusherConnection> {
  loading ??= import("./pusher-connection").then(
    (connection) => (loaded = connection),
    (cause: unknown) => {
      console.error(
        "[pusher] the connection manager did not load: no live events in this tab",
        cause,
      );
      emitPusherHealth({
        kind: "load_error",
        detail: cause instanceof Error ? cause.message : String(cause),
      });
      throw cause;
    },
  );
  return loading;
}

/**
 * Run `setup` with the connection manager: right away when it has loaded, else once it does.
 * Returns the cleanup for an effect: it runs `setup`'s own cleanup, or, before the load, makes
 * sure `setup` never runs.
 */
export function withPusherConnection(
  setup: (connection: PusherConnection) => () => void,
): () => void {
  if (loaded !== null) return setup(loaded);
  let cancelled = false;
  let cleanup: (() => void) | null = null;
  loadPusherConnection().then(
    (connection) => {
      if (!cancelled) cleanup = setup(connection);
    },
    () => undefined,
  );
  return () => {
    cancelled = true;
    cleanup?.();
  };
}
